/* eslint-disable no-magic-numbers */
const ALPHABET = "0ABCD2EFGH4IJKLMN6OPQRST8UVWXYZ-1abcd3efgh5ijklmn7opqrst9uvwxyz.";
const FIVE_CHARS = (1+2) << 4;
const THREE_CHARS = 1 << 5;

function parseNumber(arr: Array<string>, count: number): number {
	let result = 0;
	let countdown = count;

	while (countdown) {
		if (!arr.length) {
			throw new Error("No data!");
		}

		const ch = arr.pop() || " ";
		const index = ALPHABET.indexOf(ch);

		if (index === -1) {
			continue;
		}

		result <<= 6;
		result += index;
		countdown--;
	}

	return result;
}

export function stringToCoords(str: string): Array<[number, number]> {
	const results: Array<[number, number]> = [];
	const coords: [number, number] = [0, 0];
	const arr: Array<string> = str.trim().split("").reverse();
	let coordIndex = 0;

	while (arr.length) {
		// podle prvnich dvou bitu poznam, o co jde
		let num = parseNumber(arr, 1);

		// cela souradnice
		if ((num & FIVE_CHARS) === FIVE_CHARS) {
			num -= FIVE_CHARS;
			num = ((num & 0xF) << 24) + parseNumber(arr, 4);
			coords[coordIndex] = num;
		// velky posun - 16 bitu
		} else if ((num & THREE_CHARS) === THREE_CHARS) {
			num = ((num & 0xF) << 12) + parseNumber(arr, 2);
			num -= 1 << 15;
			coords[coordIndex] += num;
		// maly posun - 11 bitu
		} else {
			num = ((num & 0x1F) << 6) + parseNumber(arr, 1);
			num -= 1 << 10;
			coords[coordIndex] += num;
		}

		// mame dvojici souradnic - sup s nima do floatu
		if (coordIndex) {
			const longitude = (coords[0] * 360 / (1 << 28)) - 180;
			const latitude = (coords[1] * 180 / (1 << 28)) - 90;

			results.push([longitude, latitude]);
		}

		coordIndex = (coordIndex + 1) % 2;
	}

	return results;
}

function serializeNumber(delta: number, orig: number): string {
	let code = "";

	if (delta >= -1024 && delta < 1024) {
		code += ALPHABET.charAt((delta + 1024) >> 6);
		code += ALPHABET.charAt((delta + 1024) & 63);
	} else if (delta >= -32768 && delta < 32768) {
		const value = 0x20000 | (delta + 32768);

		code += ALPHABET.charAt((value >> 12) & 63);
		code += ALPHABET.charAt((value >> 6) & 63);
		code += ALPHABET.charAt(value & 63);
	} else {
		const value = 0x30000000 | (orig & 0xFFFFFFF);

		code += ALPHABET.charAt((value >> 24) & 63);
		code += ALPHABET.charAt((value >> 18) & 63);
		code += ALPHABET.charAt((value >> 12) & 63);
		code += ALPHABET.charAt((value >> 6) & 63);
		code += ALPHABET.charAt(value & 63);
	}

	return code;
}

export function coordsToString(arr: Array<[number, number]>): string {
	let ox = 0;
	let oy = 0;
	let result = "";

	for (let index = 0; index < arr.length; index++) {
		const coords = arr[index];
		const longitude = Math.round((coords[0] + 180) * (1 << 28) / 360);
		const latitude = Math.round((coords[1] + 90) * (1 << 28) / 180);
		const dx = longitude - ox;
		const dy = latitude - oy;

		result += serializeNumber(dx, longitude);
		result += serializeNumber(dy, latitude);

		ox = longitude;
		oy = latitude;
	}

	return result;
}

export function stringToAltitude(str: string): Array<number> {
	const results = [];
	let e0 = 0;
	const arr = str.trim().split("").reverse();
	let c1 = 0;
	let c2 = 0;
	let c3 = 0;
	let d1 = 0;
	let d2 = 0;
	let d3 = 0;
	const decNonZeroDelta = function (cx, mx) {
		return cx - mx + (cx >= mx ? 1 : 0);
	};

	while (arr.length) {
		c1 = parseNumber(arr, 1); /* podle prvnich dvou bitu poznam, o co jde */

		if ((c1 & 0x20) === 0x00) {
			const nx = 1 + ((c1 & 0x18) >> 3);

			d1 = (c1 & 0x07) - 3;

			for (let index = 0; index < nx; index++) {
				results.push(e0);
			}

			if (d1 !== 4) {
				e0 += d1;
				results.push(e0);
			}
		} else if ((c1 & 0x30) === 0x20) {
			d1 = decNonZeroDelta(c1 & 0x0F, 8);
			e0 += d1;
			results.push(e0);
		} else if ((c1 & 0x38) === 0x30) {
			c2 = parseNumber(arr, 1);
			d1 = decNonZeroDelta(c1 & 0x07, 4);
			d2 = decNonZeroDelta(c2 >> 3, 4);
			d3 = (c2 & 0x07) - (d2 < 0 ? 4 : 3);
			e0 += d1;
			results.push(e0);
			e0 += d2;
			results.push(e0);
			e0 += d3;
			results.push(e0);
		} else if ((c1 & 0x3C) === 0x38) {
			c2 = parseNumber(arr, 1);
			d1 = (c1 & 0x03) << 6;
			d2 = (c2 & 0x3F) - 128;
			e0 += d1 + d2;
			results.push(e0);
		} else {
			c2 = parseNumber(arr, 1);
			c3 = parseNumber(arr, 1);
			d1 = (c1 & 0x03) << 12;
			d2 = (c2 & 0x3F) << 6;
			d3 = (c3 & 0x3F) - 8192;
			e0 += d1 + d2 + d3;
			results.push(e0);
		}
	}

	return results;
}

function encodeInt(strIn: string, index: number): string {
	let str = strIn;
	let ui = (BigInt(index) << 1n) ^ (BigInt(index) >> 63n);

	// eslint-disable-next-line no-constant-condition
	while (true) {
		if (ui >= 32n) {
			str += ALPHABET[Number((ui & 0x1fn) | 0x20n)];
			ui >>= 5n;
		} else {
			str += ALPHABET[Number(ui)];

			return str;
		}
	}
}

export function integersToString(ints: Array<number>): string {
	let str = "";
	let index = 0;

	str = str.padEnd(ints.length, "");

	for (const value of ints) {
		const diff = value - index;

		str = encodeInt(str, diff);
		index = value;
	}

	return str;
}

function __encNonZeroDelta(delta: number, modifier: number): number {
	// All arithmetic forced to 32-bit
	if (delta > 0) {
		return (modifier + delta - 1) | 0;
	}

	return (modifier + delta) | 0;
}

export function altitudeToString(values: number[]): string {
	let elevation = "";
	let index = 0;
	let alt0 = 0;
	let prefix = 0;
	let count = 0;
	let value1 = 0;
	let value2 = 0;
	let value3 = 0;
	let alt2 = 0;
	let d1 = 0;
	let d2 = 0;

	while (index < values.length) {
		const alt1 = values[index] | 0;

		d1 = (alt1 - alt0) | 0;

		if (d1 === 0) {
			// 1b prefix '0' + 2b count(zeros) + 3b delta(next);
			prefix = 0x00;
			count = 1;
			index = (index + 1) | 0;
			while (index < values.length && alt0 === (values[index] | 0)) {
				count = (count + 1) | 0;
				index = (index + 1) | 0;
			}
			while (count >= 5) {
				value1 = (3 << 3) | 0; // count(0s) = 3+1
				value2 = (0 + 3) | 0; // delta(next) = 0
				elevation += ALPHABET.charAt((prefix + value1 + value2) | 0);
				count = (count - 5) | 0;
			}
			if (count > 0) {
				value1 = ((count - 1) << 3) | 0;
				value2 = (4 + 3) | 0; // delta(next) = skip
				if (index < values.length) {
					alt2 = values[index] | 0;
					d2 = (alt2 - alt0) | 0;
					if (d2 >= -3 && d2 <= 3) {
						value2 = (d2 + 3) | 0;
						alt0 = alt2;
						index = (index + 1) | 0;
					}
				}
				elevation += ALPHABET.charAt((prefix + value1 + value2) | 0);
			}
			continue;
		}

		if (d1 >= -4 && d1 <= 4) {
			if (index + 2 < values.length) {
				alt2 = values[index + 1] | 0;

				const alt3 = values[index + 2] | 0;

				d2 = (alt2 - alt1) | 0;

				const d3 = (alt3 - alt2) | 0;

				if (d2 > 0 && d2 <= 4 && d3 >= -3 && d3 <= 4) {
					// 3b prefix '110' + 3*3b delta;
					prefix = 0x30;
					value1 = __encNonZeroDelta(d1, 4) | 0;
					value2 = (__encNonZeroDelta(d2, 4) << 3) | 0;
					value3 = (d3 + 3) | 0;
					elevation += ALPHABET.charAt((prefix + value1) | 0);
					elevation += ALPHABET.charAt((value2 + value3) | 0);
					alt0 = alt3;
					index = (index + 3) | 0;
					continue;
				} else if (d2 < 0 && d2 >= -4 && d3 >= -4 && d3 <= 3) {
					// 3b prefix '110' + 3*3b delta;
					prefix = 0x30;
					value1 = __encNonZeroDelta(d1, 4) | 0;
					value2 = (__encNonZeroDelta(d2, 4) << 3) | 0;
					value3 = (d3 + 4) | 0;
					elevation += ALPHABET.charAt((prefix + value1) | 0);
					elevation += ALPHABET.charAt((value2 + value3) | 0);
					alt0 = alt3;
					index = (index + 3) | 0;
					continue;
				}
			}
		}

		const da = d1;

		if (da >= -8 && da <= 8) {
		// 2b prefix '10' + 4b delta;
			prefix = 0x20;
			value1 = __encNonZeroDelta(da, 8) | 0;
			elevation += ALPHABET.charAt((prefix + value1) | 0);
		} else if (da >= -128 && da < 128) {
		// 4b prefix '1110' + 8b delta;
			prefix = 0x38;
			value1 = (((da + 128) >> 6) & 0x03) | 0;
			value2 = ((da + 128) & 0x3F) | 0;
			elevation += ALPHABET.charAt((prefix + value1) | 0);
			elevation += ALPHABET.charAt(value2);
		} else {
		// 4b prefix '1111' + 14b altitude;
			prefix = 0x3C;
			value1 = (((da + 8192) >> 12) & 0x03) | 0;
			value2 = (((da + 8192) >> 6) & 0x3F) | 0;
			value3 = ((da + 8192) & 0x3F) | 0;
			elevation += ALPHABET.charAt((prefix + value1) | 0);
			elevation += ALPHABET.charAt(value2);
			elevation += ALPHABET.charAt(value3);
		}
		alt0 = (alt0 + d1) | 0;
		index = (index + 1) | 0;
	}

	return elevation;
}
